import type { Api, ClientState, Content, RhemaClient } from "@rhema/client";
import { useEffect, useState } from "react";
import { useApi, useClient, useClientState } from "../session/session";

export function assistantChatIn(state: ClientState) {
  return Object.values(state.chats).find((c) => c.kind === "assistant");
}

/** The person's Queravel chat, created on first use. Only they are in it. */
export async function ensureAssistantChat(api: Api, client: RhemaClient): Promise<string> {
  const known = assistantChatIn(client.getState());
  if (known) return known.id;
  const chat = await api.assistantChat();
  await client.refreshChat(chat.id);
  return chat.id;
}

/** Keeps a Queravel conversation in the person's Queravel chat, encrypted like any message. */
export function keepTurns(client: RhemaClient, chatId: string, contents: Content[]) {
  if (!contents.length) return;
  void client.sendAll(chatId, contents).catch(() => undefined);
}

/** Finds or creates the Queravel chat and loads its messages while `active` is true. */
export function useAssistantChat(active: boolean) {
  const api = useApi();
  const client = useClient();
  const state = useClientState();
  const [created, setCreated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const chatId = assistantChatIn(state)?.id ?? created;

  useEffect(() => {
    if (!active || chatId) return;
    let current = true;
    ensureAssistantChat(api, client)
      .then((id) => current && setCreated(id))
      .catch((e: Error) => current && setError(e.message));
    return () => {
      current = false;
    };
  }, [active, chatId, api, client]);

  useEffect(() => {
    if (active && chatId) client.openChat(chatId).catch(() => undefined);
  }, [active, chatId, client]);

  return { chatId, error };
}
